// ============================================================
// onPlate.js
// "On plate" — posts supplied welded to a base plate instead of set in the
// ground. The office used to hand-edit the description and hand-add $50, and
// both steps got forgotten. Now it is a tick on the line.
//
// The user ticks it wherever it belongs. NOTHING here guesses which products
// are posts: no category match, no description sniffing. That kind of guessing
// is what went wrong with the round-up table and the NON-STOCK badge.
//
// ── WHY THE DERIVED VALUES ARE STORED, NOT COMPUTED AT RENDER ──
//
// Deriving the description and price from the flag at render would be the
// cleaner shape, and it was the first choice. It does not work here: a line
// item's `description` and `unitPrice` are read DIRECTLY, as stored, by screens
// far outside the two document editors —
//   • ViewDocument: the printed invoice/quote rows, the packing slip rows, and
//     the collected-items list;
//   • Shop.js: the picking list on the tablet;
//   • the inventory dialogs and anything else reading a saved document.
// Deriving at render would mean every one of those had to learn about the flag,
// and any that was missed would print the wrong price on a real invoice.
//
// So the stored `description` and `unitPrice` ARE the plated ones — every
// existing reader is correct with no change at all — and the ORIGINALS are kept
// beside them:
//   onPlate                 true
//   onPlateBaseDescription  the description before " ON PLATE" was appended
//   onPlateBasePrice        the unit price before the surcharge
//
// Because the originals are kept intact rather than reverse-engineered,
// unticking restores them EXACTLY, and ticking twice cannot double anything:
// every function below is idempotent and re-derives from the base, never from
// the current displayed value.
// ============================================================

import { round2 } from './lineMath';

// ── THE SURCHARGE ──
// $50 per post, added to the UNIT price — the quantity multiplies it, so six
// posts on plates add $300 to the line, not $50. One named constant: the price
// is a business decision that will change, and it must change in one place.
export const ON_PLATE_SURCHARGE = 50;

/** Appended to the description. The leading space belongs to the suffix. */
export const ON_PLATE_SUFFIX = ' ON PLATE';

// Case-insensitive, and tolerant of stray spacing, so a description somebody
// typed by hand before this feature existed is recognised rather than doubled.
const SUFFIX_RE = /\s*ON PLATE\s*$/i;

const stripSuffix = (text) => String(text ?? '').replace(SUFFIX_RE, '');
const endsWithSuffix = (text) => SUFFIX_RE.test(String(text ?? ''));
const num = (v) => parseFloat(v) || 0;

/** Is this line on plate? A line saved before the field existed is not. */
export const isOnPlate = (item) => item?.onPlate === true;

/**
 * Tick or untick a line.
 *
 * Idempotent in both directions: ticking an already-ticked line returns it
 * unchanged, so the $50 cannot be added twice and the suffix cannot be appended
 * twice, however the caller is wired.
 *
 * Text notes are refused — they carry no price or quantity (the editors already
 * exclude them from the item editor for the same reason), so a surcharge on one
 * would be money on a line that contributes nothing.
 */
export function setLineOnPlate(item, on) {
  if (!item || item.isTextNote) return item;
  if (!!on === isOnPlate(item)) return item;

  if (on) {
    const baseDescription = item.description ?? '';
    const basePrice = num(item.unitPrice);
    return {
      ...item,
      onPlate: true,
      // Kept verbatim — this is what unticking restores.
      onPlateBaseDescription: baseDescription,
      onPlateBasePrice: basePrice,
      // Appended ONCE, here, and only when it is not already at the end — so a
      // description somebody typed the suffix into by hand is left alone rather
      // than gaining a second copy.
      description: endsWithSuffix(baseDescription)
        ? baseDescription
        : `${baseDescription}${ON_PLATE_SUFFIX}`,
      unitPrice: round2(basePrice + ON_PLATE_SURCHARGE),
    };
  }

  // Untick: restore the originals. The fallbacks only matter for a line whose
  // base fields were lost somehow — strip the suffix and take the surcharge
  // back off rather than leaving the plated values in place.
  const restoredDescription = item.onPlateBaseDescription !== undefined
    ? item.onPlateBaseDescription
    : stripSuffix(item.description);
  const restoredPrice = item.onPlateBasePrice !== undefined
    ? item.onPlateBasePrice
    : Math.max(0, round2(num(item.unitPrice) - ON_PLATE_SURCHARGE));

  const next = { ...item, description: restoredDescription, unitPrice: restoredPrice };
  delete next.onPlate;
  delete next.onPlateBaseDescription;
  delete next.onPlateBasePrice;
  return next;
}

/**
 * Apply a field edit from the line-item row, keeping the base values true.
 *
 * On a line that is NOT on plate this is a plain field set — identical to what
 * the editors did before.
 *
 * On a plated line, what the user types is the DISPLAYED value, because that is
 * what the input shows. NEITHER FIELD IS EVER REWRITTEN UNDER THE CURSOR — the
 * suffix and the surcharge are applied when the box is ticked and removed when
 * it is unticked; in between, both fields belong to the user. Only the stored
 * BASE moves, so unticking still knows what to go back to:
 *   description — stored verbatim; the base becomes the text with a trailing
 *                 suffix removed if it has one. Delete the suffix and it stays
 *                 deleted; type it back and it is not doubled.
 *   unitPrice   — stored verbatim; the base becomes typed − 50 (never
 *                 negative). Unticking then gives that base.
 *
 * An earlier version re-appended the suffix on every description change. As
 * soon as a user deleted one character the text no longer ended in the full
 * suffix, so it was not recognised as present and was appended again:
 * "POST ON PLAT ON PLATE", and worse with every further keystroke. Never
 * rewrite a field the user is typing in.
 */
export function editLineField(item, field, value) {
  if (!item) return item;
  if (!isOnPlate(item)) return { ...item, [field]: value };

  if (field === 'description') {
    return {
      ...item,
      // Exactly what they typed. No append, no strip, no correction.
      description: value,
      // What unticking will restore: their text, less a trailing suffix if it
      // is still there. If they deleted it, this IS their text, so unticking
      // changes nothing — which is the sensible reading of "they took it off".
      onPlateBaseDescription: stripSuffix(value),
    };
  }
  if (field === 'unitPrice') {
    // The raw string stays in the field so typing feels normal (an empty box
    // mid-edit is not rewritten under the cursor).
    return {
      ...item,
      unitPrice: value,
      onPlateBasePrice: Math.max(0, round2(num(value) - ON_PLATE_SURCHARGE)),
    };
  }
  return { ...item, [field]: value };
}

/**
 * Set a line's BASE price — for the paths that re-price from a product rather
 * than from the user's keystrokes: a price-level change, and "reset to price
 * level".
 *
 * On a plated line the surcharge is re-applied on top, so re-pricing can never
 * silently drop the $50 while the line still reads ON PLATE.
 */
export function setLineBasePrice(item, basePrice) {
  if (!item) return item;
  if (!isOnPlate(item)) return { ...item, unitPrice: basePrice };
  return {
    ...item,
    onPlateBasePrice: basePrice,
    unitPrice: round2(num(basePrice) + ON_PLATE_SURCHARGE),
  };
}

/**
 * Reconcile a whole item returned by the line-item editor modal, which edits
 * the displayed description and price and knows nothing about plates.
 *
 * Same rule as editLineField: what came back is the displayed value, so the
 * bases are re-derived from it. A line that was NOT plated passes through
 * untouched.
 */
export function reconcileEditedLine(edited) {
  if (!isOnPlate(edited)) return edited;
  let out = editLineField(edited, 'description', edited.description);
  out = editLineField(out, 'unitPrice', out.unitPrice);
  return out;
}
